import crc32 from '../lib/crc32';
import emitter from './emitter';
import { ABTestError } from './error';
import { activeVariants, experimentConfigs, playedExperiments, resetExperiments, variantsDefinedExperiments } from './state';
/**
 * Subscribes to a specified event with a callback function and returns a function to unsubscribe from the event.
 */
export const subscribe = (event, callback) => {
    emitter.on(event, callback);
    return () => {
        emitter.off(event, callback);
    };
};
export const subscribeOnce = (event, callback) => {
    emitter.once(event, callback);
    return () => {
        emitter.off(event, callback);
    };
};
export const emitWin = (experimentName) => {
    if (typeof experimentName !== 'string') {
        throw new Error("Required argument 'experimentName' should have type 'string'");
    }
    const value = activeVariants.get(experimentName);
    if (value == null) {
        throw new Error(`User currently is not in experiment “${experimentName}”`);
    }
    emitter.emit('win', experimentName, value);
};
export const emitPlayOnFirstTime = (experimentName, variantName) => {
    if (typeof experimentName !== 'string') {
        throw new Error("Required argument 'experimentName' should have type 'string'");
    }
    if (typeof variantName !== 'string') {
        throw new Error("Required argument 'variantName' should have type 'string'");
    }
    if (!playedExperiments.has(experimentName)) {
        playedExperiments.add(experimentName);
        emitter.emit('play', experimentName, variantName);
    }
};
export const reset = () => {
    resetExperiments();
    emitter.removeAllListeners();
};
export const defineVariants = (experimentName, variantNames, variantWeights) => {
    const variantsNamesSet = new Set(variantNames);
    if (variantsNamesSet.size !== variantNames.length) {
        throw new Error('Variant names should be unique');
    }
    if (variantWeights && variantWeights.length !== variantNames.length) {
        throw new Error('Variant weights should be provided for all variants');
    }
    const experiment = variantNames.reduce((map, variantName, index) => {
        map.set(variantName, variantWeights?.[index] ?? 1);
        return map;
    }, new Map());
    experimentConfigs.set(experimentName, experiment);
    variantsDefinedExperiments.add(experimentName);
};
export const getDefinedVariants = (experimentName) => {
    const experiment = experimentConfigs.get(experimentName);
    if (!experiment) {
        return undefined;
    }
    return new Set(experiment.keys());
};
const getSortedVariantsWithWeights = (experimentName) => {
    const experiment = experimentConfigs.get(experimentName);
    if (!experiment) {
        return [];
    }
    return Array.from(experiment.entries()).sort(([a], [b]) => a.localeCompare(b));
};
export const getActiveVariant = (experimentName) => {
    return activeVariants.get(experimentName);
};
export const setActiveVariant = (experimentName, variantName, skipSave) => {
    activeVariants.set(experimentName, variantName);
    emitter.emit('active-variant', experimentName, variantName, skipSave);
};
export const addExperimentVariant = (experimentName, variantName, variantWeight = 1) => {
    const experiment = experimentConfigs.get(experimentName) ?? new Map();
    if (experiment.has(variantName)) {
        return;
    }
    if (variantsDefinedExperiments.has(experimentName)) {
        throw new ABTestError('INVALID_VARIANT', `Experiment “${experimentName}” added new variants after variants were defined.`);
    }
    if (activeVariants.has(experimentName)) {
        throw new ABTestError('INVALID_VARIANT', `Experiment “${experimentName}” added new variants after a variant was selected. Declare the variant names using emitter.defineVariants(experimentName, variantNames).`);
    }
    experiment.set(variantName, variantWeight);
    experimentConfigs.set(experimentName, experiment);
};
export const getVariantRandomly = (experimentName, userIdentifier) => {
    /*
  
    Choosing a weighted variant:
      variantWithWeights = [A, 4], [B, 8], [C, 2]
      weightSum = 14
      weightedIndex = 9
  
      AAAABBBBBBBBCC
      ========^
      Select B
  
    */
    const variantWithWeights = getSortedVariantsWithWeights(experimentName);
    const weightSum = variantWithWeights.reduce((sum, [, weight]) => sum + weight, 0);
    // A random number between 0 and weightSum
    let weightedIndex = userIdentifier
        ? // hash userIdentifier with experimentName to prevent the same remainder for all experiments for same user
            Math.abs(crc32(experimentName + userIdentifier) % weightSum)
        : Math.floor(Math.random() * weightSum);
    // Default to the last variant
    let selectedVariant = variantWithWeights[variantWithWeights.length - 1][0];
    // Select the variant based on the weighted index
    for (let index = 0; index < variantWithWeights.length; index++) {
        weightedIndex -= variantWithWeights[index][1];
        if (weightedIndex < 0) {
            selectedVariant = variantWithWeights[index][0];
            break;
        }
    }
    return selectedVariant;
};
